import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { 
  AlertTriangle, 
  CheckCircle2, 
  Info, 
  Plus, 
  X,
  Pill,
  AlertCircle,
  Shield,
  Camera,
  Upload,
  RefreshCw,
  Settings,
  FileText,
  ChevronDown,
  ChevronUp,
  AlertOctagon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Import deterministic engine
import { evaluateAllMedications, formatResultsForDisplay, MODIFIER_DEFINITIONS, DOMAIN_LABELS, getSeverityLabel } from './engine/stackingEngine';
import { getRuleMetadata } from './engine/ruleEngine';
import { parseMedicationInput, addMedicationsFromInput, validateMedicationList, detectMultipleMedications, extractMedicationsFromText } from './engine/inputParser';
import drugAliases from './data/drugAliases.json';

// Custom Icon - Pill & Syringe crossed
const MedAdvisorIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.5 3.5L3.5 10.5C2.1 11.9 2.1 14.1 3.5 15.5L8.5 20.5C9.9 21.9 12.1 21.9 13.5 20.5L20.5 13.5C21.9 12.1 21.9 9.9 20.5 8.5L15.5 3.5C14.1 2.1 11.9 2.1 10.5 3.5Z" />
    <line x1="8" y1="8" x2="16" y2="16" />
    <line x1="19" y1="5" x2="22" y2="2" strokeWidth="2.5" />
    <line x1="17" y1="7" x2="19" y2="5" strokeWidth="2" />
  </svg>
);

// Severity color config
const getSeverityConfig = (severity) => {
  const configs = {
    GREEN: {
      icon: CheckCircle2,
      bgClass: "bg-emerald-50",
      textClass: "text-emerald-700",
      borderClass: "border-emerald-400",
      badgeBg: "bg-emerald-100",
      color: "#059669"
    },
    YELLOW: {
      icon: Info,
      bgClass: "bg-amber-50",
      textClass: "text-amber-700",
      borderClass: "border-amber-400",
      badgeBg: "bg-amber-100",
      color: "#D97706"
    },
    ORANGE: {
      icon: AlertTriangle,
      bgClass: "bg-orange-50",
      textClass: "text-orange-700",
      borderClass: "border-orange-400",
      badgeBg: "bg-orange-100",
      color: "#EA580C"
    },
    RED: {
      icon: AlertCircle,
      bgClass: "bg-red-50",
      textClass: "text-red-700",
      borderClass: "border-red-600",
      badgeBg: "bg-red-100",
      color: "#DC2626"
    },
    UNKNOWN: {
      icon: AlertOctagon,
      bgClass: "bg-slate-50",
      textClass: "text-slate-700",
      borderClass: "border-slate-400",
      badgeBg: "bg-slate-200",
      color: "#64748b"
    }
  };
  return configs[severity] || configs.UNKNOWN;
};

// Domain Risk Card
const DomainRiskCard = ({ domain, expanded, onToggle }) => {
  const config = getSeverityConfig(domain.severity);
  const Icon = config.icon;
  
  return (
    <Card className={`border-l-4 ${config.borderClass} transition-all duration-200`}>
      <CardHeader 
        className="p-3 cursor-pointer hover:bg-slate-50/50" 
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${config.textClass}`} />
            <span className="text-sm font-semibold text-slate-800">{domain.domain}</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${config.badgeBg} ${config.textClass} text-[10px] font-semibold uppercase`}>
              {domain.severityLabel}
            </Badge>
            {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </div>
        </div>
        {domain.modifierApplied && (
          <div className="mt-1 text-[10px] text-orange-600 flex items-center gap-1">
            <AlertOctagon className="h-3 w-3" />
            Escalated by: {domain.appliedModifiers.join(', ')}
          </div>
        )}
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0 px-3 pb-3 border-t border-slate-100">
          <div className="space-y-2 mt-2">
            <div>
              <p className="text-[10px] font-medium text-slate-500 uppercase">Contributing Medications</p>
              <p className="text-xs text-slate-700">{domain.medications}</p>
            </div>
            <div>
              <p className="text-[10px] font-medium text-slate-500 uppercase">Recommendation</p>
              <p className="text-xs text-slate-700">{domain.recommendation}</p>
            </div>
            <div>
              <p className="text-[10px] font-medium text-slate-500 uppercase">Advisory Note</p>
              <p className="text-xs text-slate-700">{domain.advisoryNote}</p>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Unclassified Warning
const UnclassifiedWarning = ({ medications }) => {
  if (!medications || medications.length === 0) return null;
  
  return (
    <Card className="border-l-4 border-slate-400 bg-slate-50">
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          <AlertOctagon className="h-4 w-4 text-slate-600 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-slate-700">Unrecognized Medications</p>
            <p className="text-[10px] text-slate-600 mt-1">
              The following medications were not recognized and require manual review:
            </p>
            <div className="flex flex-wrap gap-1 mt-2">
              {medications.map((med, i) => (
                <Badge key={i} variant="outline" className="text-[10px] border-slate-400 text-slate-600">
                  {med}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

async function saveMedicationsToSheets(medicationArray) {
  const webAppUrl = "https://script.google.com/macros/s/AKfycbzEFsim3cLGaezsN09qFcTfMvhUOONIjLn0ZFacuphPV12bVnEYZvDNKsb9P6F40_d7/exec";
  
  const payload = {
    medications: medicationArray.map(m => m.replace(/ \(Unknown\)$/i, '').trim())
  };

  try {
    const response = await fetch(webAppUrl, {
      method: "POST",
      mode: "cors", 
      redirect: "follow", // CRITICAL: Google redirects requests internally; this tells fetch to follow it
      headers: {
        // Trick the browser to skip CORS preflight
        "Content-Type": "text/plain;charset=utf-8" 
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();
 
  } catch (error) {
    console.error("Failed to report", error);
  }
}

// Main Page Component
const Home = () => {
  const [medications, setMedications] = useState([]);
  const [medicationInput, setMedicationInput] = useState("");
  const [traumaLevel, setTraumaLevel] = useState("T2");
  const [activeModifiers, setActiveModifiers] = useState([]);
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [expandedDomains, setExpandedDomains] = useState({});
  const [showModifiers, setShowModifiers] = useState(false);

  const handleMedicationKeyDown = (e) => {
    if (e.key === "Enter" && medicationInput.trim()) {
      e.preventDefault();
      handleAddMedications(medicationInput);
      setMedicationInput("");
    }
  };

  const handleMedicationPaste = (e) => {
    const pastedText = e.clipboardData.getData('text');
    
    const medNames = Object.values(drugAliases.aliases || drugAliases).flatMap(entry => entry.aliases || []);
    const { medications: extracted, reportOnly } = extractMedicationsFromText(pastedText, medNames);

    if (reportOnly.length > 0) saveMedicationsToSheets(reportOnly);

    if (extracted.length > 0) {
      e.preventDefault();
      handleAddMedications(extracted.join('\n'));
      setMedicationInput("");
    } else {
      const detected = detectMultipleMedications(pastedText);
      if (detected.isMultiple) {
        e.preventDefault();
        handleAddMedications(pastedText);
        setMedicationInput("");
      }
    }
  };

  // Centralized medication adding logic
  const handleAddMedications = (input) => {
    const result = addMedicationsFromInput(medications, input);
    
    if (result.added > 0) {
      setMedications(result.medications);
      
      if (result.added > 1) {
        toast.success(`Added ${result.added} medications`);
      }
      
      if (result.duplicates > 0) {
        toast.info(`${result.duplicates} duplicate(s) skipped`);
      }
    } else if (result.duplicates > 0) {
      toast.info("Medication already added");
    }
  };

  const removeMedication = (med) => {
    setMedications(medications.filter(m => m !== med));
  };

  const toggleModifier = (modifierId) => {
    setActiveModifiers(prev => 
      prev.includes(modifierId) 
        ? prev.filter(m => m !== modifierId)
        : [...prev, modifierId]
    );
  };

  const analyzeRisks = () => {
    const validation = validateMedicationList(medications);
    
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    // Run deterministic evaluation
    const evaluation = evaluateAllMedications(medications, traumaLevel, activeModifiers);
    const formatted = formatResultsForDisplay(evaluation);
    
    if (formatted.unclassified && formatted.unclassified.length > 0) {
      saveMedicationsToSheets(formatted.unclassified);
    }
    
    setResults(formatted);
    setShowResults(true);
    
    // Expand all domains by default
    const expanded = {};
    formatted.domains.forEach(d => { expanded[d.domain] = true; });
    setExpandedDomains(expanded);
    
    toast.success("Analysis complete (deterministic)");
  };

  const resetForm = () => {
    setMedications([]);
    setMedicationInput("");
    setActiveModifiers([]);
    setResults(null);
    setShowResults(false);
    setExpandedDomains({});
  };

  const toggleDomain = (domain) => {
    setExpandedDomains(prev => ({ ...prev, [domain]: !prev[domain] }));
  };

  const overallConfig = results ? getSeverityConfig(results.overallSeverity) : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex flex-col items-center justify-center p-4 md:p-8">
      <div className="w-full max-w-[440px] bg-white rounded-xl shadow-xl overflow-hidden border border-emerald-200" data-testid="extension-container">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <MedAdvisorIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="font-semibold text-sm tracking-tight">Medivigil</h1>
                <p className="text-[10px] text-emerald-100">Deterministic Risk Assessment</p>
              </div>
            </div>
            <Badge className="bg-white/20 text-white text-[9px] border-0">
              Rule-Based v1.0
            </Badge>
          </div>
        </div>

        <ScrollArea className="h-[520px]">
          <div className="p-4 space-y-4">
            {/* Trauma Level — always visible, re-runs analysis when results are showing */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-medium text-emerald-700 uppercase tracking-wide flex items-center gap-1">
                <FileText className="h-3 w-3" /> Procedure Trauma Level
              </label>
              <Select
                value={traumaLevel}
                onValueChange={(val) => {
                  setTraumaLevel(val);
                  if (showResults && medications.length > 0) {
                    const evaluation = evaluateAllMedications(medications, val, activeModifiers);
                    setResults(formatResultsForDisplay(evaluation));
                  }
                }}
              >
                <SelectTrigger className="h-10 text-sm border-emerald-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="T1">
                    <span className="font-medium">T1 - Minimal Trauma</span>
                    <span className="text-slate-500 ml-2 text-xs">(Scaling, exam, simple resto)</span>
                  </SelectItem>
                  <SelectItem value="T2">
                    <span className="font-medium">T2 - Moderate Trauma</span>
                    <span className="text-slate-500 ml-2 text-xs">(Simple extraction, SRP)</span>
                  </SelectItem>
                  <SelectItem value="T3">
                    <span className="font-medium">T3 - High Trauma</span>
                    <span className="text-slate-500 ml-2 text-xs">(Surgical ext, implant, bone surgery)</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!showResults ? (
              <>
                {/* Medications */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-medium text-emerald-700 uppercase tracking-wide flex items-center gap-1">
                    <Pill className="h-3 w-3" /> Patient Medications
                  </label>
                  <div className="flex flex-wrap gap-1.5 p-2 bg-emerald-50/50 border border-emerald-200 rounded-lg min-h-[44px]">
                    {medications.map((med, i) => (
                      <Badge key={i} className="bg-emerald-600 text-white hover:bg-emerald-700 px-2 py-0.5 text-[10px] flex items-center gap-1">
                        {med}
                        <button onClick={() => removeMedication(med)} className="ml-0.5 hover:text-red-200">
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </Badge>
                    ))}
                    <input
                      type="text"
                      value={medicationInput}
                      onChange={(e) => setMedicationInput(e.target.value)}
                      onKeyDown={handleMedicationKeyDown}
                      onPaste={handleMedicationPaste}
                      placeholder={medications.length === 0 ? "Type or paste medications" : "Add more..."}
                      className="flex-1 min-w-[100px] border-0 bg-transparent p-0 h-6 text-xs focus:ring-0 focus:outline-none"
                      data-testid="medication-input"
                    />
                  </div>
                  <p className="text-[9px] text-slate-500">
                    Paste multiple: newline, comma, or semicolon separated
                  </p>
                </div>

                {/* Modifiers Section */}
                <div className="space-y-1.5">
                  <button 
                    onClick={() => setShowModifiers(!showModifiers)}
                    className="text-[10px] font-medium text-emerald-700 uppercase tracking-wide flex items-center gap-1 hover:text-emerald-800"
                  >
                    <Settings className="h-3 w-3" /> 
                    Clinical Modifiers
                    {showModifiers ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    {activeModifiers.length > 0 && (
                      <Badge className="bg-orange-100 text-orange-700 text-[9px] ml-1">{activeModifiers.length} active</Badge>
                    )}
                  </button>
                  
                  {showModifiers && (
                    <div className="space-y-2 p-2 bg-slate-50 rounded-lg border border-slate-200">
                      {Object.values(MODIFIER_DEFINITIONS).map((modifier) => (
                        <div key={modifier.id} className="flex items-start gap-2">
                          <Checkbox 
                            id={modifier.id}
                            checked={activeModifiers.includes(modifier.id)}
                            onCheckedChange={() => toggleModifier(modifier.id)}
                            className="mt-0.5"
                          />
                          <label htmlFor={modifier.id} className="text-xs cursor-pointer">
                            <span className="font-medium text-slate-700">{modifier.label}</span>
                            <p className="text-[10px] text-slate-500">{modifier.description}</p>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Analyze Button */}
                <Button
                  onClick={analyzeRisks}
                  disabled={medications.length === 0}
                  className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-sm font-medium"
                  data-testid="analyze-button"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Evaluate Risks
                </Button>
              </>
            ) : results ? (
              <div className="space-y-3">
                {/* Assessment Warning (if partial or all unrecognized) */}
                {results.assessmentWarning && (
                  <Card className={`border-l-4 ${results.assessmentStatus === 'ALL_UNRECOGNIZED' ? 'border-red-400 bg-red-50' : 'border-amber-400 bg-amber-50'}`}>
                    <CardContent className="p-3">
                      <div className="flex items-start gap-2">
                        <AlertOctagon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${results.assessmentStatus === 'ALL_UNRECOGNIZED' ? 'text-red-600' : 'text-amber-600'}`} />
                        <div>
                          <p className={`text-xs font-semibold ${results.assessmentStatus === 'ALL_UNRECOGNIZED' ? 'text-red-700' : 'text-amber-700'}`}>
                            {results.assessmentStatus === 'ALL_UNRECOGNIZED' ? 'Manual Review Required' : 'Incomplete Assessment'}
                          </p>
                          <p className={`text-[10px] mt-0.5 ${results.assessmentStatus === 'ALL_UNRECOGNIZED' ? 'text-red-600' : 'text-amber-600'}`}>
                            {results.assessmentWarning}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Overall Summary */}
                <Card className={`${overallConfig.bgClass} border ${overallConfig.borderClass}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="text-xs font-semibold text-slate-800">Overall Risk Assessment</h3>
                        <p className="text-[10px] text-slate-500">{results.traumaLevelLabel}</p>
                      </div>
                      <Badge className={`${overallConfig.badgeBg} ${overallConfig.textClass} font-semibold text-xs uppercase px-2 py-1`}>
                        {getSeverityLabel(results.overallSeverity)}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {medications.map((med, i) => (
                        <Badge key={i} variant="outline" className="text-[9px] border-slate-300">{med}</Badge>
                      ))}
                    </div>
                    {/* Recognition stats */}
                    <p className="text-[9px] text-slate-400 mt-2">
                      {results.recognizedCount}/{results.totalMedications} medications recognized
                    </p>
                  </CardContent>
                </Card>

                {/* Unclassified Warning */}
                <UnclassifiedWarning medications={results.unclassified} />

                {/* Domain Results */}
                <div className="space-y-2">
                  <p className="text-[10px] font-medium text-slate-500 uppercase">Risk by Domain</p>
                  {results.domains.length > 0 ? (
                    results.domains.map((domain) => (
                      <DomainRiskCard 
                        key={domain.domain}
                        domain={domain}
                        expanded={expandedDomains[domain.domain]}
                        onToggle={() => toggleDomain(domain.domain)}
                      />
                    ))
                  ) : (
                    <Card className="border-slate-200">
                      <CardContent className="p-3 text-center">
                        <p className="text-xs text-slate-500">
                          {results.assessmentStatus === 'ALL_UNRECOGNIZED' 
                            ? 'Unable to assess risks. All medications require manual review.'
                            : 'No specific risks identified for recognized medications.'}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* New Analysis Button */}
                <Button 
                  onClick={resetForm} 
                  variant="outline" 
                  className="w-full h-9 border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" /> New Assessment
                </Button>
              </div>
            ) : null}
          </div>
        </ScrollArea>

        {/* Disclaimer */}
        <div className="bg-emerald-50 border-t border-emerald-100 px-3 py-2">
          <div className="flex items-start gap-1.5">
            <Shield className="h-3 w-3 text-emerald-600 mt-0.5 flex-shrink-0" />
            <p className="text-[9px] text-emerald-700 leading-relaxed">
              <strong>Advisory Only:</strong> Deterministic rule-based assessment. Does not replace clinical judgment. 
              No data stored. All processing client-side.
            </p>
          </div>
        </div>
      </div>
      
      {/* Website Link */}
      <a 
        href="https://www.medivigil.com" 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-[11px] text-emerald-600 mt-3 text-center hover:text-emerald-700 hover:underline transition-colors flex items-center justify-center gap-1"
      >
        <MedAdvisorIcon className="h-3.5 w-3.5" />
        Learn more at www.medivigil.com
      </a>
      <Toaster position="top-center" />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
